import {
  useState,
  useEffect,
  ChangeEvent,
  ComponentPropsWithoutRef,
  forwardRef,
  ForwardedRef
} from 'react';
import { typedBoolean } from 'utils/array';
import styled from 'styled-components';
import { HelperText, ErrorText, Limit } from '../Input/style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Flexbox } from '@morressier/ts';
import { MAX_BIOGRAPHY_CHARACTER_LIMIT } from 'utils/constants';

const StyledTextArea = styled.textarea<{ hasError: boolean }>`
  border: 1px solid ${({ hasError }) =>
    hasError ? 'var(--color-red-50)' : 'var(--color-mono-60)'};
  border-radius: 4px;
  padding: 8px;
  padding-bottom: 1.5rem;
  width: 100%;
  &:hover, &:focus-within {
    border 1px solid ${({ hasError }) =>
      hasError ? 'var(--color-red-50)' : 'var(--color-mono-0)'};
  }
  font-size: var(--fontSizes-normal);
  line-height: var(--lineHeights-normal);
  color: var(--color-mono-0);
  &::placeholder {
    color: var(--color-mono-60);
  }
`;

export const ErrorIcon = styled(FontAwesomeIcon)`
  font-size: var(--fontSizes-normal);
  line-height: var(--lineHeights-l);
  font-weight: bold;
  margin-right: 4px;
  color: var(--color-red-50);
`;
interface TextAreaProps extends ComponentPropsWithoutRef<'textarea'> {
  value?: string;
  limit?: number;
  error?: string;
}

const TextArea = (
  props: TextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>
) => {
  const [internalValue, setInternalValue] = useState<string | number>('');
  // Set default limit
  const {
    limit = MAX_BIOGRAPHY_CHARACTER_LIMIT,
    error,
    value,
    onChange,
    ...rest
  } = props;

  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    }
    setInternalValue(e.target.value);
  };

  const valueLength = (internalValue ?? '').toString().length;

  return (
    <>
      <StyledTextArea
        hasError={typedBoolean(error)}
        value={internalValue}
        onChange={handleChange}
        ref={ref}
        {...rest}
      />
      {(limit || error) && (
        <HelperText>
          {Boolean(error) && (
            <Flexbox alignItems="center">
              <ErrorIcon icon={faSquareExclamation} />
              <ErrorText>{error}</ErrorText>
            </Flexbox>
          )}
          {Boolean(limit) && (
            <Limit isLimitExceeded={valueLength > limit}>
              {valueLength} / {limit}
            </Limit>
          )}
        </HelperText>
      )}
    </>
  );
};

export default forwardRef(TextArea);
